import type {
  ApprovalDetail,
  ApprovalItem,
  ApprovalListResponse,
  ListQuery,
  QueueType,
} from '../api/approvals';
import type { PaymentRequest, PaymentStatus } from '../api/payments';

const PR_KEY = 'erp_ac_mock_payments';
const HISTORY_KEY = 'erp_ac_mock_payments_history';
const CEO_THRESHOLD = 3_000_000;

export function isMockMode(): boolean {
  if (!import.meta.env.DEV) return false;
  if (localStorage.getItem('erp_ac_use_real_api') === '1') return false;
  return localStorage.getItem('erp_ac_dev_user') !== null;
}

function readPRs(): PaymentRequest[] {
  try {
    const raw = localStorage.getItem(PR_KEY);
    if (raw) return JSON.parse(raw) as PaymentRequest[];
  } catch {
    /* ignore */
  }
  return [];
}

function writePRs(items: PaymentRequest[]) {
  localStorage.setItem(PR_KEY, JSON.stringify(items));
}

function readHistory(prId: string) {
  const key = `${HISTORY_KEY}_${prId}`;
  return JSON.parse(localStorage.getItem(key) || '[]') as ApprovalDetail['history'];
}

function addHistory(
  prId: string,
  action: string,
  fromStatus: string | null,
  toStatus: string | null,
  note?: string,
) {
  const user = currentUser();
  const key = `${HISTORY_KEY}_${prId}`;
  const list = JSON.parse(localStorage.getItem(key) || '[]') as ApprovalDetail['history'];
  list!.push({
    id: `hist-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    actorId: user?.id || 'system',
    actorName: user?.name || 'System',
    actorRole: user?.role || 'NV',
    action,
    fromStatus,
    toStatus,
    note,
    createdAt: new Date().toISOString(),
  });
  localStorage.setItem(key, JSON.stringify(list));
}

function currentUser() {
  try {
    return JSON.parse(localStorage.getItem('erp_ac_dev_user') || 'null') as {
      id: string;
      name: string;
      role: 'KTT' | 'NV_KT' | 'CEO' | 'NV';
    } | null;
  } catch {
    return null;
  }
}

function queueOf(pr: PaymentRequest): QueueType {
  if (pr.urgency) return 'urgent';
  if (pr.type === 'tam_ung' && pr.advanceType === 'urgent') return 'urgent';
  return 'batch';
}

/** Map PR đang chờ duyệt → Approval item cho role tương ứng (chỉ trả khi PENDING). */
function synthesize(pr: PaymentRequest): ApprovalItem | null {
  if (pr.status !== 'PENDING_KTT' && pr.status !== 'PENDING_CEO') return null;
  const approverRole = pr.status === 'PENDING_CEO' ? 'CEO' : 'KTT';
  return {
    id: `apr-${pr.id}-${approverRole}`,
    paymentRequestId: pr.id,
    approverRole,
    approverId: null,
    approverName: null,
    action: null,
    note: null,
    queueType: queueOf(pr),
    status: 'PENDING',
    createdAt: pr.submittedAt || pr.createdAt,
    actedAt: null,
    paymentRequest: pr,
  };
}

/** Synthesize cho tab historical (Đã duyệt / Đã từ chối). */
function synthesizeHistorical(pr: PaymentRequest, action: 'approve' | 'reject'): ApprovalItem {
  return {
    id: `apr-${pr.id}-hist`,
    paymentRequestId: pr.id,
    approverRole: pr.amount >= 3_000_000 ? 'CEO' : 'KTT',
    approverId: null,
    approverName: null,
    action,
    note: null,
    queueType: queueOf(pr),
    status: 'DONE',
    createdAt: pr.submittedAt || pr.createdAt,
    actedAt: pr.submittedAt || pr.createdAt,
    paymentRequest: pr,
  };
}

export const mockApprovalsApi = {
  async list(q: ListQuery): Promise<ApprovalListResponse> {
    const user = currentUser();
    const role = user?.role;
    if (role !== 'KTT' && role !== 'CEO') {
      return {
        urgentCount: 0,
        batchCount: 0,
        tabCounts: { pending: 0, approved: 0, rejected: 0, all: 0 },
        items: [],
        paging: { page: 1, limit: 20, total: 0, totalPages: 0, hasNextPage: false },
      };
    }

    const prs = readPRs();
    const pendingStatus: PaymentStatus = role === 'CEO' ? 'PENDING_CEO' : 'PENDING_KTT';

    // Đếm cho các tab (không phụ thuộc tab đang chọn)
    const pendingItems = prs
      .filter((p) => p.status === pendingStatus)
      .map(synthesize)
      .filter((a): a is ApprovalItem => a !== null);
    const approvedItems = prs
      .filter((p) => p.status === 'APPROVED' || p.status === 'DISBURSED')
      .map((p) => synthesizeHistorical(p, 'approve'));
    const rejectedItems = prs
      .filter((p) => p.status === 'REJECTED')
      .map((p) => synthesizeHistorical(p, 'reject'));

    const tabCounts = {
      pending: pendingItems.length,
      approved: approvedItems.length,
      rejected: rejectedItems.length,
      all: pendingItems.length + approvedItems.length + rejectedItems.length,
    };

    // Chọn tab
    const tab = q.tab || 'pending';
    let items: ApprovalItem[];
    if (tab === 'pending') items = [...pendingItems];
    else if (tab === 'approved') items = [...approvedItems];
    else if (tab === 'rejected') items = [...rejectedItems];
    else items = [...pendingItems, ...approvedItems, ...rejectedItems];

    const urgentCount = pendingItems.filter((a) => a.queueType === 'urgent').length;
    const batchCount = pendingItems.filter((a) => a.queueType === 'batch').length;

    // Search
    if (q.search) {
      const s = q.search.toLowerCase();
      items = items.filter((a) => {
        const pr = a.paymentRequest;
        return (
          pr.code.toLowerCase().includes(s) ||
          pr.reason.toLowerCase().includes(s) ||
          (pr.requesterName || '').toLowerCase().includes(s)
        );
      });
    }

    // Queue filter chỉ áp cho tab pending
    if (tab === 'pending' && q.queue && q.queue !== 'all') {
      items = items.filter((a) => a.queueType === q.queue);
    }

    // Sort: urgent trước, mới nhất trước
    items.sort((a, b) => {
      if (tab === 'pending' && a.queueType !== b.queueType) {
        return a.queueType === 'urgent' ? -1 : 1;
      }
      return b.createdAt.localeCompare(a.createdAt);
    });

    const page = q.page || 1;
    const limit = q.limit || 20;
    const total = items.length;
    const paged = items.slice((page - 1) * limit, page * limit);

    return {
      urgentCount,
      batchCount,
      tabCounts,
      items: paged,
      paging: {
        page,
        limit,
        total,
        totalPages: Math.ceil(total / limit),
        hasNextPage: page * limit < total,
      },
    };
  },

  async get(id: string): Promise<ApprovalDetail> {
    const prs = readPRs();
    const prId = id.replace(/^apr-/, '').replace(/-(KTT|CEO)$/, '');
    const pr = prs.find((p) => p.id === prId);
    if (!pr) throw new Error('Approval không tồn tại');
    const synth = synthesize(pr);
    if (!synth) throw new Error('PR không còn ở trạng thái chờ duyệt');
    return { ...synth, history: readHistory(pr.id) };
  },

  async approve(id: string, note?: string): Promise<ApprovalItem> {
    const user = currentUser();
    if (!user) throw new Error('Chưa login');
    const prs = readPRs();
    const prId = id.replace(/^apr-/, '').replace(/-(KTT|CEO)$/, '');
    const idx = prs.findIndex((p) => p.id === prId);
    if (idx === -1) throw new Error('Không tìm thấy PR');
    const pr = prs[idx];

    let nextStatus: PaymentStatus;
    if (pr.status === 'PENDING_KTT' && pr.amount >= CEO_THRESHOLD) {
      nextStatus = 'PENDING_CEO';
    } else {
      nextStatus = 'APPROVED';
    }

    prs[idx] = { ...pr, status: nextStatus };
    writePRs(prs);
    addHistory(pr.id, 'approve', pr.status, nextStatus, note);

    const synth = synthesize(prs[idx]);
    return synth ?? {
      id,
      paymentRequestId: pr.id,
      approverRole: user.role === 'CEO' ? 'CEO' : 'KTT',
      approverId: user.id,
      approverName: user.name,
      action: 'approve',
      note: note ?? null,
      queueType: queueOf(pr),
      status: 'DONE',
      createdAt: pr.submittedAt || pr.createdAt,
      actedAt: new Date().toISOString(),
      paymentRequest: prs[idx],
    };
  },

  async reject(id: string, reason: string): Promise<ApprovalItem> {
    const user = currentUser();
    if (!user) throw new Error('Chưa login');
    if (!reason || reason.length < 10) throw new Error('Lý do từ chối tối thiểu 10 ký tự');
    const prs = readPRs();
    const prId = id.replace(/^apr-/, '').replace(/-(KTT|CEO)$/, '');
    const idx = prs.findIndex((p) => p.id === prId);
    if (idx === -1) throw new Error('Không tìm thấy PR');
    const pr = prs[idx];

    prs[idx] = { ...pr, status: 'REJECTED' };
    writePRs(prs);
    addHistory(pr.id, 'reject', pr.status, 'REJECTED', reason);

    return {
      id,
      paymentRequestId: pr.id,
      approverRole: user.role === 'CEO' ? 'CEO' : 'KTT',
      approverId: user.id,
      approverName: user.name,
      action: 'reject',
      note: reason,
      queueType: queueOf(pr),
      status: 'DONE',
      createdAt: pr.submittedAt || pr.createdAt,
      actedAt: new Date().toISOString(),
      paymentRequest: prs[idx],
    };
  },

  async bulkApprove(ids: string[], note?: string): Promise<{ approved: number; failed: string[] }> {
    let approved = 0;
    const failed: string[] = [];
    for (const id of ids) {
      try {
        await this.approve(id, note);
        approved++;
      } catch {
        failed.push(id);
      }
    }
    return { approved, failed };
  },
};
